import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Filter } from '@playq/octopus-common';
import { QuerySpacesResponse, Space, SpacesSort } from '@playq/octopus2-apps';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { iteratorMaxLimit } from '/constants';

import { spacesServiceQueryKeys } from './constants';
import { createNewSpacesResponseAfterSpaceDeleting, mutateFeaturesAfterSpaceDeleting } from './helpers';

const emptyArray: [] = [];

const defaultQuery = {
  iterator: new OffsetLimit({ limit: iteratorMaxLimit, offset: 0 }),
  sortBy: [],
  filterBy: {},
};

export const useSpacesQuery = (
  iterator: OffsetLimit = defaultQuery.iterator,
  sortBy: SpacesSort[] = defaultQuery.sortBy,
  filterBy: { [key: string]: Filter } = defaultQuery.filterBy,
  options?: IUseQueryOptionsExtended<QuerySpacesResponse | undefined, GenericFailure | Error>
) => {
  const queryClient = useQueryClient();

  const keys = useMemo(() => spacesServiceQueryKeys.concat(iterator, sortBy, filterBy), [iterator, sortBy, filterBy]);

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.spacesService.querySpaces(iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch,
    total,
    args: [iterator, sortBy, filterBy],
    serviceMethod: services2.spacesService.querySpaces.bind(services2.spacesService),
  });

  const mutateDeletedSpace = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (s?: Space) => s?.id,
    getEntities: (r: QuerySpacesResponse) => r.entities,
    setEntities: (r: QuerySpacesResponse, newEntities: Space[]) => {
      r.entities = newEntities;
    },
    createNewResponse: createNewSpacesResponseAfterSpaceDeleting,
    mutateCustomFields: mutateFeaturesAfterSpaceDeleting,
  });

  const spaces = res.data ? res.data.entities : emptyArray;

  return {
    ...res,
    features: res.data?.features ?? {},
    spaces,
    total,
    tags: keys,
    mutate,
    mutateDeletedSpace,
    refetch,
  };
};
